import {  useEffect, useCallback, useState, useRef, HTMLAttributes } from 'react'
import Technique from "@/assets/technique.png";
import NameplateScanRealistic from "@/assets/nameplate-scan-realistic.png";
import ThaloIcon from "@/assets/thalo_icon.svg";
import { Button } from "../ui/button";
import { Camera, Edit, Sparkles, UploadIcon } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  useUploadNameplateScan,
  useNameplateScan,
  useUploadNameplateScanWithOrganizationId,
} from "@/installFlow/nameplateScan/api";
import { ScanStatus, TaskStatus } from "@/installFlow/nameplateScan/types";
import { DialogDescription } from '@radix-ui/react-dialog';
import {
  installFlowIdentificationResult,
  installFlowScanId,
  installFlowTechniquePhoto,
} from "@/installFlow/storage-keys";

interface AuthProps extends HTMLAttributes<HTMLDivElement> {}

export function NamePlatePhoto({ className }: AuthProps) {
  const navigate = useNavigate();
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [scanId, setScanId] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  const uploadMutation = useUploadNameplateScanWithOrganizationId(14);
  const { data: scan } = useNameplateScan(scanId, {
    enabled: !!scanId,
  });

  const isIdentifying =
    uploadMutation.isPending ||
    (!!scanId &&
      scan?.identification_status !== TaskStatus.COMPLETED &&
      scan?.identification_status !== TaskStatus.FAILED);

  // When identification_status becomes completed, log and navigate
  useEffect(() => {
    if (!scan) return;
    if (
      scan.identification_status === TaskStatus.COMPLETED ||
      scan.status === ScanStatus.FAILED
    ) {
      if(scan.identification_result && scan.identification_result.confidence >= 0.6) {

        console.log("Nameplate scan result:", scan);
        if (scan.identification_result) {
          installFlowIdentificationResult.set(
            JSON.stringify(scan.identification_result)
          );
        }
        navigate({ to: "/installFlow/review-name-plate-info" });
      } else {
        setShowErrorDialog(true);
      }

    }
  }, [scan]);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    return () => stopStream();
  }, [stopStream]);

  async function uploadAndPoll(file: File) {
    try {
      const result = await uploadMutation.mutateAsync(file);
      setScanId(result.id);
      installFlowScanId.set(result.id);
    } catch (err) {
      console.error("Failed to upload nameplate scan:", err);
    }
  }

  async function openCamera() {
    setCameraError(null);
    setCameraOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;
      await new Promise<void>((resolve, reject) => {
        if (!videoRef.current) {
          reject(new Error("Video element not ready"));
          return;
        }
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play().then(resolve).catch(reject);
        };
        videoRef.current.onerror = () =>
          reject(new Error("Video failed to load"));
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not access camera";
      setCameraError(
        message.includes("Permission") || message.includes("NotAllowed")
          ? "Camera access was denied. Please allow camera in your browser settings."
          : "Could not access camera. Please check permissions and try again.",
      );
      setCameraOpen(false);
    }
  }

  function handleCloseCamera(open: boolean) {
    if (!open) {
      stopStream();
      setCameraError(null);
      setCameraOpen(false);
    }
  }
  const handleErrorDialogClose = () => {
    setShowErrorDialog(false);
  };

  async function capturePhoto() {
    const video = videoRef.current;
    if (!video || !streamRef.current || isCapturing) return;
    setIsCapturing(true);
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      setIsCapturing(false);
      return;
    }
    ctx.drawImage(video, 0, 0);

    canvas.toBlob(async (blob) => {
      if (!blob) {
        setIsCapturing(false);
        return;
      }
      const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
      if (typeof sessionStorage !== "undefined") {
        installFlowTechniquePhoto.set(dataUrl);
      }
      stopStream();
      setCameraOpen(false);
      setIsCapturing(false);

      const file = new File([blob], "nameplate.jpg", { type: "image/jpeg" });
      await uploadAndPoll(file);
    }, "image/jpeg", 0.9);
  }

  function handleUploadPhotoClick() {
    fileInputRef.current?.click();
  }

  function handleUploadPhotoChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      if (typeof sessionStorage !== "undefined") {
        installFlowTechniquePhoto.set(dataUrl);
      }
    };
    reader.readAsDataURL(file);

    uploadAndPoll(file);
    event.target.value = "";
  }

  function goToEnterNamePlateInfo() {
    navigate({ to: "/installFlow/enter-name-plate-info" });
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        aria-hidden
        onChange={handleUploadPhotoChange}
      />

      {isIdentifying ? (
        <div className="w-full max-w-[460px] min-h-screen flex flex-col items-center justify-center gap-6">
          <img
            src={ThaloIcon}
            alt="Thalo"
            className="w-24 h-24 animate-bounce"
          />
          <p className="text-lg font-bold text-black">Reading nameplate data...</p>
        </div>
      ) : (
      <div className="w-full max-w-[460px] h-full flex flex-col gap-6">
        <img src={NameplateScanRealistic} alt="Technique" />
        <div className="text-left px-6 gap-3 flex flex-col">
          <h2 className="text-xl font-bold text-black leading-[26px]">
            Next, take a photo of the asset's nameplate.
          </h2>
          <p className="text-base text-[#727272] leading-[20px] font-semibold">
            The nameplate is a small label attached to the HVAC unit. We'll
            automatically read the information, like manufacturer and model
            number.
          </p>
        </div>
        <div className="w-full flex flex-col gap-2 px-6">
          <Button
            className="w-full h-[90px] p-4 flex items-center justify-center bg-[#0053EE] text-white"
            onClick={openCamera}
          >
            <Camera className="w-5 h-5 mr-4" />
            Take Photo
          </Button>
          <div className="w-full flex gap-4">
            <Button
              className="w-1/2 h-[90px] p-4 flex flex-col items-center justify-center border-2 border-[rgba(0, 0, 0, 0.1)] bg-white text-black"
              onClick={handleUploadPhotoClick}
            >
              <UploadIcon className="w-5 h-5 mr-4" />
              Upload photo
            </Button>
            <Button
              className="w-1/2 h-[90px] p-4 flex flex-col items-center justify-center border-2 border-[rgba(0, 0, 0, 0.1)] bg-white text-black"
              onClick={goToEnterNamePlateInfo}
            >
              <Edit className="w-5 h-5 mr-4" />
              Enter Manually
            </Button>
          </div>
        </div>
      </div>
      )}

      <Dialog open={cameraOpen} onOpenChange={handleCloseCamera}>
        <DialogContent
          className="sm:max-w-[500px]"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Take nameplate photo</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="relative w-full aspect-[4/3] bg-black rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
            </div>
            {cameraError && (
              <p className="text-sm text-destructive">{cameraError}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={capturePhoto}
              disabled={isCapturing || !!cameraError}
              className="w-full bg-[#0053EE] text-white"
            >
              {isCapturing ? "Capturing…" : "Capture photo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-thalo-blueLight" />
              Nameplate Scanned
            </DialogTitle>
            <DialogDescription>
              {scan?.identification_result && scan?.identification_result.confidence < 0.6
                ? "Your image quality is less than 60%, therefore the quality is not recommended." 
                : scan?.status === "failed"
                  ? "We couldn't read the nameplate. You can still continue and enter details manually."
                  : ""}
            </DialogDescription>
          </DialogHeader>

        
          {scan?.identification_result ? (
            scan?.identification_result.confidence < 0.6 ? (
              <div className="text-sm text-muted-foreground py-2 flex flex-col items-center justify-center">
                <p>
                  Please take a new one so we can process the information
                  correctly.
                </p>
                <Button variant="thalo" onClick={handleErrorDialogClose}>
                  Retake
                </Button>
              </div>
            ) : (
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={handleErrorDialogClose}>
                  Retake
                </Button>
                
              </div>
            )
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
